"use client";

import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { ArtistIcon, UnderlineTabBar } from "@/components/design-system/ArtistDesignSystem";
import { CareerHeroHeader } from "./CareerHeroHeader";
import { CollectionsGridView } from "./CollectionsGridView";
import { CreatePlaylistSheet } from "./CreatePlaylistSheet";
import { LibrarySkeleton } from "./LibrarySkeleton";
import { LibraryTopBar } from "./LibraryTopBar";
import {
	buildPlaceholderLibraryData,
	buildUserLibraryData,
	LIBRARY_TAB_IDS,
	LIBRARY_TAB_LABELS,
	type LibraryData,
	type LibraryNote,
	type LibraryPlaylist,
	type LibraryTabId,
} from "./library-data";
import {
	browserSocialRepository,
	scopeKey,
} from "@/domain/social/SocialRepository";
import { NotesListView } from "./NotesListView";
import { SignInRequiredState } from "./SignInRequiredState";
import { WorksGridView } from "./WorksGridView";
import { useRouter } from "@/infrastructure/router/navigation";
import {
	beginnerTutorialStore,
	circleDanceTutorial,
	tutorialGuideText,
	tutorialText,
	useBeginnerTutorial,
} from "@/domain/tutorial/BeginnerTutorial";
import { TutorialCoachmark } from "@/components/tutorial/BeginnerTutorialUI";

const pageStyle: CSSProperties = {
	width: "100%",
};

const tabsWrapStyle: CSSProperties = {
	position: "sticky",
	top: 0,
	zIndex: 5,
	background: "var(--ds-background)",
};

const contentWrapStyle: CSSProperties = {
	paddingBottom: "calc(118px + env(safe-area-inset-bottom))",
};

const LIBRARY_STORAGE_PREFIX = "artist.library.v1";

/**
 * LibraryExperience — full personal-hub Library page matching iOS LibraryView.
 *
 * Layout: CareerHeroHeader (hero bg + gradient + metric panel + profile
 * header) → DSUnderlineTabBar (4 tabs: 作品/笔记/收藏/好听) → tab content
 * (WorksGridView / NotesListView / CollectionsGridView / WorksGridView).
 *
 * States: skeleton while profile loads · placeholder profile + sign-in
 * required gate for sign-in-gated tabs when anonymous (笔记 stays offline) ·
 * CreatePlaylistSheet slide-over when the 收藏 tab's plus button is tapped.
 */
export function LibraryExperience() {
	const auth = useArtistSession();
	const router = useRouter();
	const tutorial = useBeginnerTutorial();
	const [data, setData] = useState<LibraryData | null>(null);
	const [isProfileLoading, setIsProfileLoading] = useState(true);
	const [selectedTab, setSelectedTab] = useState<LibraryTabId>("works");
	const [showAllMetrics, setShowAllMetrics] = useState(false);
	const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);

	const isSignedIn = auth.phase === "user";

	useEffect(() => {
		let active = true;
		setIsProfileLoading(true);
		const scope = auth.scope;
		if (!isSignedIn || scope === null) {
			setData(buildPlaceholderLibraryData());
			setIsProfileLoading(false);
			return () => {
				active = false;
			};
		}
		void browserSocialRepository
			.loadProfile(scope)
			.then(({ value }) => {
				if (!active) return;
				const base = buildUserLibraryData(
					scope.ownerUserId,
					value.displayName,
					value.bio.trim().length > 0 ? value.bio : null,
				);
				setData(readStoredLibrary(scopeKey(scope), base));
				setIsProfileLoading(false);
			})
			.catch(() => {
				if (!active) return;
				setData(
					buildUserLibraryData(scope.ownerUserId, auth.session?.email ?? "Artist", null),
				);
				setIsProfileLoading(false);
			});
		return () => {
			active = false;
		};
	}, [auth.scope, auth.session?.email, isSignedIn]);

	const tutorialWorksAvailable = tutorial.localTrackCreated && selectedTab === "works";
	const requiresSignIn = !isSignedIn && selectedTab !== "notes" && !tutorialWorksAvailable;

	const displayData = useMemo<LibraryData | null>(() => {
		if (data === null || !tutorial.localTrackCreated) return data;
		const tutorialTrack = {
			id: circleDanceTutorial.localTrackId,
			title: "Circle Dance",
			artworkURL: circleDanceTutorial.assets.cover.public_url,
			likeCount: 0,
			isLiked: false,
		};
		if (tutorial.step === "openTutorialTrack") {
			return { ...data, works: [tutorialTrack] };
		}
		if (tutorial.claimedTrackId !== null && data.works.some((track) => track.id === tutorial.claimedTrackId)) {
			return data;
		}
		return {
			...data,
			works: [tutorialTrack, ...data.works.filter((track) => track.id !== tutorialTrack.id)],
		};
	}, [data, tutorial.claimedTrackId, tutorial.localTrackCreated, tutorial.step]);

	const handleCreatePlaylist = (name: string, _description: string | null) => {
		if (data === null) return;
		const newPlaylist: LibraryPlaylist = {
			id: `playlist-${Date.now()}`,
			name,
			coverImageURL: null,
			trackCount: 0,
			isSystemPlaylist: false,
			isDefaultFavorites: false,
		};
		const next = { ...data, playlists: [...data.playlists, newPlaylist] };
		setData(next);
		persistLibrary(auth.scope === null ? null : scopeKey(auth.scope), next);
		setShowCreatePlaylist(false);
	};

	const handleDeleteNote = (note: LibraryNote) => {
		if (data === null) return;
		const next = {
			...data,
			notes: data.notes.filter((item) => item.id !== note.id),
		};
		setData(next);
		persistLibrary(auth.scope === null ? null : scopeKey(auth.scope), next);
	};

	const showSkeleton = isProfileLoading || displayData === null;

	return (
		<div className="library-experience" style={pageStyle}>
			{showSkeleton ? (
				<LibrarySkeleton />
			) : (
				<>
					<div style={{ position: "relative" }}>
						{tutorial.step === "inactive" ? <LibraryTopBar
							showCreatePlaylist={selectedTab === "collections"}
							onOpenCreatePlaylist={() => {
								if (!isSignedIn) {
									window.location.assign("/auth");
									return;
								}
								setShowCreatePlaylist(true);
							}}
						/> : (
							<button
								type="button"
								className="ds-button ds-button--secondary"
								style={{ position: "absolute", top: "max(12px, env(safe-area-inset-top, 0px))", left: 16, zIndex: 20 }}
								onClick={beginnerTutorialStore.exit}
							>
								<ArtistIcon name="close" size={16} />
								<span>{tutorialText("退出教学模式", "退出教學模式", "Exit tutorial mode")}</span>
							</button>
						)}
						<CareerHeroHeader
							profile={displayData.profile}
							metrics={displayData.metrics}
							showAllMetrics={showAllMetrics}
							onToggleShowAllMetrics={() =>
								setShowAllMetrics((value) => !value)
							}
						/>
					</div>

					<div className="library-tabs-wrap" style={tabsWrapStyle}>
						<UnderlineTabBar<LibraryTabId>
							tabs={LIBRARY_TAB_IDS}
							selected={selectedTab}
							onSelect={setSelectedTab}
							label={(value) => LIBRARY_TAB_LABELS[value]}
							className="library-tabs"
						/>
					</div>

					<div className="library-content" style={contentWrapStyle}>
						{requiresSignIn ? (
							<SignInRequiredState />
						) : (
							<LibraryTabContent
								tab={selectedTab}
								data={displayData}
								onDeleteNote={handleDeleteNote}
								onOpenTutorialTrack={() => {
									beginnerTutorialStore.advance("openTutorialTrack", "imagineIntro");
									router.push("/tutorial/circle-dance");
								}}
							/>
						)}
					</div>

					{showCreatePlaylist ? (
						<CreatePlaylistSheet
							onClose={() => setShowCreatePlaylist(false)}
							onCreate={handleCreatePlaylist}
						/>
					) : null}
					{tutorial.step === "openTutorialTrack" ? (
						<TutorialCoachmark step={tutorial.step} text={tutorialGuideText(tutorial.step)} position="bottom" />
					) : null}
				</>
			)}
		</div>
	);
}

function readStoredLibrary(key: string, base: LibraryData): LibraryData {
	try {
		const raw = window.localStorage.getItem(`${LIBRARY_STORAGE_PREFIX}:${key}`);
		if (raw === null) return base;
		const value = JSON.parse(raw) as Partial<LibraryData>;
		return {
			...base,
			works: Array.isArray(value.works) ? value.works : base.works,
			liked: Array.isArray(value.liked) ? value.liked : base.liked,
			playlists: Array.isArray(value.playlists) ? value.playlists : base.playlists,
			notes: Array.isArray(value.notes) ? value.notes : base.notes,
		};
	} catch {
		return base;
	}
}

function persistLibrary(key: string | null, data: LibraryData): void {
	if (key === null) return;
	window.localStorage.setItem(
		`${LIBRARY_STORAGE_PREFIX}:${key}`,
		JSON.stringify({
			works: data.works,
			liked: data.liked,
			playlists: data.playlists,
			notes: data.notes,
		}),
	);
}

function LibraryTabContent({
	tab,
	data,
	onDeleteNote,
	onOpenTutorialTrack,
}: Readonly<{
	tab: LibraryTabId;
	data: LibraryData;
	onDeleteNote: (note: LibraryNote) => void;
	onOpenTutorialTrack: () => void;
}>) {
	switch (tab) {
		case "works":
			return (
				<WorksGridView
					tracks={data.works}
					onTrackTap={(track) => {
						if (track.id === circleDanceTutorial.localTrackId) onOpenTutorialTrack();
					}}
					emptyTitle="还没有作品"
					emptyMessage="创作完成后，作品会出现在这里。"
				/>
			);
		case "notes":
			return (
				<NotesListView
					notes={data.notes}
					onDelete={onDeleteNote}
					emptyTitle="还没有笔记"
					emptyMessage="写下灵感或采风片段，私人笔记仅你可见。"
				/>
			);
		case "collections":
			return (
				<CollectionsGridView
					playlists={data.playlists}
					emptyTitle="还没有收藏夹"
					emptyMessage="把喜欢的作品整理进歌单，默认收藏夹会自动置顶。"
				/>
			);
		case "liked":
			return (
				<WorksGridView
					tracks={data.liked}
					emptyTitle="还没有好听"
					emptyMessage="点亮作品的心形按钮，它会出现在这里。"
				/>
			);
	}
}
